import { UserIdentityModel } from "client/Api/UserProfile/service";
import {
	TimeZone,
	getSystemTimeZone,
	resolveTimeZoneWithFallbackToEtcEra
} from "client/DateAndTime/timezones";
import { isNotNil } from "client/Misc/utils";
import { getCurrentCulture } from "client_legacy/ndaw/culture";
import {
	waitForUserConfigurationToInitialize
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import create, { UseStore } from "zustand";
import createVanilla, { StoreApi } from "zustand/vanilla";

export type UserProfileStoreData = {
	currentUserIdentity: UserIdentityModel | null;
	userTimezone: TimeZone;
	setCurrentUserIdentity(patch: Partial<Omit<UserIdentityModel, "userId">>): void;
	setUserTimezone(timeZone: TimeZone | null): void;
};

const culture: string = getCurrentCulture();
const systemTimezone: TimeZone = getSystemTimeZone(culture);

export const userProfileStore: StoreApi<UserProfileStoreData> = createVanilla<UserProfileStoreData>(
	(set): UserProfileStoreData => {
		waitForUserConfigurationToInitialize()
			.then(
				userConfiguration => {
					const {
						currentUserIdentity,
						timeZone
					} = userConfiguration;

					set({
						currentUserIdentity,
						userTimezone: isNotNil(timeZone)
							? resolveTimeZoneWithFallbackToEtcEra(timeZone.identifier, culture, timeZone.utcOffsetInMinutes)
							: systemTimezone
					});
				}
			);

		return {
			currentUserIdentity: null,
			userTimezone: systemTimezone,
			setCurrentUserIdentity(patch: Partial<UserIdentityModel>): void {
				set(c => ({
					currentUserIdentity: isNotNil(c.currentUserIdentity)
						? {
							...c.currentUserIdentity,
							...cleanUpUndefinedProps(patch)
						}
						: null
				}));
			},
			setUserTimezone(timeZone: TimeZone | null): void {
				set({
					userTimezone: timeZone ?? systemTimezone
				});
			}
		};
	}
);

export const useUserProfileStore: UseStore<UserProfileStoreData> = create(userProfileStore);

function cleanUpUndefinedProps(patch: Partial<UserIdentityModel>): Partial<UserIdentityModel> {
	const patchCopy: Partial<UserIdentityModel> = {
		...patch
	};

	for (const key in patchCopy) {
		if (Object.prototype.hasOwnProperty.call(patchCopy, key)) {
			if (patchCopy[key as StringKeyOf<UserIdentityModel>] === undefined) {
				delete patchCopy[key as StringKeyOf<UserIdentityModel>];
			}
		}
	}

	return patchCopy;
}